import React from "react";
import * as MUI from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { getStockStatus, formatCurrency } from "../utils/stockStatus";
import { getMeasuringUnitLabel } from "../utils/measuringUnits";
import DetailField from "../../../../../../components/display/DetailField";
import SectionCard from "../../../../../../components/display/SectionCard";

function FlagChip({ label, active }) {
  return (
    <MUI.Chip
      icon={active ? <CheckCircleOutlineIcon /> : <HighlightOffOutlinedIcon />}
      label={label}
      size="small"
      color={active ? "success" : "error"}
    />
  );
}

/** Full info display for a warehouse item; shared by the popup dialog and the full page. */
export default function WarehouseItemDetailsContent({ item, currency }) {
  const { t } = useTranslation();
  const status = item ? getStockStatus(item.quantity) : null;

  return (
    <MUI.Stack spacing={2.5}>
      {status && status.key !== "ok" && (
        <MUI.Alert severity={status.color} sx={{ borderRadius: 2.5 }}>
          {t(status.label)}
        </MUI.Alert>
      )}

      <SectionCard label={t("Details")}>
        <MUI.Stack spacing={2}>
          <DetailField
            label={t("Description")}
            value={item?.description || "-"}
          />
          <MUI.Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              rowGap: 2,
              columnGap: 2,
            }}
          >
            <DetailField label={t("Supplier")} value={item?.supplier?.name} />
            <DetailField label={t("Category")} value={item?.category?.name} />
          </MUI.Box>
        </MUI.Stack>
      </SectionCard>

      <SectionCard label={t("InventoryDetails")}>
        <MUI.Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            rowGap: 2,
            columnGap: 2,
          }}
        >
          <DetailField label={t("Code")} value={item?.code || "-"} />
          <DetailField
            label={t("SerialNumber")}
            value={item?.serialNumber || "-"}
          />
          <DetailField
            label={t("MeasuringUnit")}
            value={getMeasuringUnitLabel(item?.measuringUnit, t) || "-"}
          />
          <DetailField
            label={t("ExpirationDate")}
            value={
              item?.expirationDate
                ? dayjs(item.expirationDate).format("DD/MM/YYYY")
                : "-"
            }
          />
        </MUI.Box>
      </SectionCard>

      <SectionCard label={t("PricingAndStock")}>
        <MUI.Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
            rowGap: 2.5,
            columnGap: 2,
          }}
        >
          <DetailField
            label={t("Price")}
            value={item ? formatCurrency(item.price, currency) : ""}
          />
          <DetailField label={t("PDV")} value={`${Number(item?.pdv)}%`} />
          <DetailField
            label={t("Discount")}
            value={`${Number(item?.discount)}%`}
          />
          <DetailField
            label={t("DiscountedPrice")}
            value={`${formatCurrency(item?.discountedPrice, currency)}`}
            emphasize
          />
          <DetailField
            label={t("PriceWithVat")}
            value={item ? formatCurrency(item.priceWithTax, currency) : ""}
            emphasize
          />
          <DetailField
            label={t("DiscountedPriceWithTax")}
            value={
              item ? formatCurrency(item.discountedPriceWithTax, currency) : ""
            }
            emphasize
          />
          <DetailField label={t("Quantity")} value={item?.quantity} />
        </MUI.Box>
      </SectionCard>

      <SectionCard label={t("AdditionalInformation")}>
        <MUI.Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <FlagChip label={t("Sellable")} active={Boolean(item?.sellable)} />
          <FlagChip
            label={t("AntiRabiesVaccine")}
            active={Boolean(item?.antiRabiesVaccine)}
          />
          <FlagChip
            label={t("OtherVaccine")}
            active={Boolean(item?.otherVaccine)}
          />
          <FlagChip
            label={t("NarcoticDrug")}
            active={Boolean(item?.narcoticDrug)}
          />
          <FlagChip
            label={t("PrescriptionRequired")}
            active={Boolean(item?.prescriptionRequired)}
          />
        </MUI.Stack>
      </SectionCard>
    </MUI.Stack>
  );
}
